import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, ImagePlus, Trash2 } from 'lucide-react';
import type { CategoryDto, I18n, ImageDto, ProductDetailDto, ProductListItemDto, RebuildBundlesResultDto, UnresolvedBundleDto, VariantEditDto, VendorDto, VideoDto } from '@thebb/shared';
import { get, post, put, del, upload } from '../api';
import { useAuth } from '../auth';
import { Badge, ErrorBox, Modal, PageTitle, Spinner } from '../components/ui';
import { HtmlEditor } from '../components/HtmlEditor';

// --- shared bits (also used by the storefront pages) -------------------------------------------------

export const kr = (minor: number | null | undefined) => (minor == null ? '' : (minor / 100).toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
export const toMinor = (s: string): number | null => {
  const t = s.trim().replace(/\./g, '').replace(',', '.');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
};

/** A money field in kroner that reports minor units on blur. */
export function MoneyInput({ value, onChange, className = 'input w-28 text-right', disabled }: { value: number | null | undefined; onChange: (v: number | null) => void; className?: string; disabled?: boolean }) {
  const [text, setText] = useState(kr(value));
  useEffect(() => setText(kr(value)), [value]);
  return <input className={className} value={text} disabled={disabled} inputMode="decimal" onChange={(e) => setText(e.target.value)} onBlur={() => onChange(toMinor(text))} />;
}

/** One text in Danish and English, switched by small tabs. */
export function I18nInput({ value, onChange, multiline, html, rows = 4, placeholder }: { value: I18n; onChange: (v: I18n) => void; multiline?: boolean; /** WYSIWYG (Jodit) instead of a textarea. */ html?: boolean; rows?: number; placeholder?: string }) {
  const [lang, setLang] = useState<'da' | 'en'>('da');
  const v = value[lang] ?? '';
  return (
    <div>
      <div className="mb-1 flex gap-1 text-xs">
        {(['da', 'en'] as const).map((l) => (
          <button key={l} type="button" className={`rounded px-2 py-0.5 ${lang === l ? 'bg-primary text-white' : 'bg-canvas text-muted'}`} onClick={() => setLang(l)}>
            {l.toUpperCase()}{!value[l] && l !== lang ? ' ·' : ''}
          </button>
        ))}
      </div>
      {html ? (
        <HtmlEditor value={v} onChange={(h) => onChange({ ...value, [lang]: h })} height={rows * 40} />
      ) : multiline ? (
        <textarea className="input font-mono text-xs" rows={rows} value={v} placeholder={placeholder} onChange={(e) => onChange({ ...value, [lang]: e.target.value })} />
      ) : (
        <input className="input" value={v} placeholder={placeholder} onChange={(e) => onChange({ ...value, [lang]: e.target.value })} />
      )}
    </div>
  );
}

/** Upload button that stores an image and hands back its dto. */
export function ImageUploadButton({ onUploaded, onError, label = 'Upload billede' }: { onUploaded: (img: ImageDto) => void; onError: (e: unknown) => void; label?: string }) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  return (
    <>
      <input
        ref={ref}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={async (e) => {
          const files = [...(e.target.files ?? [])];
          e.target.value = '';
          setBusy(true);
          try {
            for (const f of files) onUploaded(await upload<ImageDto>('/catalogue/images', f));
          } catch (err) {
            onError(err);
          } finally {
            setBusy(false);
          }
        }}
      />
      <button type="button" className="btn" disabled={busy} onClick={() => ref.current?.click()}>
        <ImagePlus className="h-4 w-4" /> {busy ? 'Uploader…' : label}
      </button>
    </>
  );
}

/** Upload of a product video (mp4 or webm, up to 300 MB). */
export function VideoUploadButton({ onUploaded, onError }: { onUploaded: (v: VideoDto) => void; onError: (e: unknown) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  return (
    <>
      <input
        ref={ref}
        type="file"
        accept="video/mp4,video/webm,video/quicktime"
        className="hidden"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (!f) return;
          setBusy(true);
          try {
            onUploaded(await upload<VideoDto>('/catalogue/videos', f));
          } catch (err) {
            onError(err);
          } finally {
            setBusy(false);
          }
        }}
      />
      <button type="button" className="btn" disabled={busy} onClick={() => ref.current?.click()}>{busy ? 'Uploader video…' : 'Upload video'}</button>
    </>
  );
}

/** A single image slot (logo, hero, category image). */
export function SingleImage({ image, onChange, onError }: { image: ImageDto | null; onChange: (img: ImageDto | null) => void; onError: (e: unknown) => void }) {
  return (
    <div className="flex items-center gap-3">
      {image ? <img src={image.thumbUrl} alt="" className="h-16 w-16 rounded border border-line object-contain" /> : <div className="flex h-16 w-16 items-center justify-center rounded border border-dashed border-line text-xs text-muted">intet</div>}
      <ImageUploadButton label={image ? 'Skift' : 'Upload'} onUploaded={onChange} onError={onError} />
      {image && <button type="button" className="btn" onClick={() => onChange(null)}>Fjern</button>}
    </div>
  );
}

// --- products ---------------------------------------------------------------------------------------

type ProductPage = { items: ProductListItemDto[]; total: number; page: number; perPage: number };

/** CAT-01: the product list. Products from 3x stay synced until someone edits them here. */
export function ProductsPage() {
  const { can } = useAuth();
  const [q, setQ] = useState('');
  const [owned, setOwned] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [page, setPage] = useState(1);
  const params = new URLSearchParams({ page: String(page), perPage: '50' });
  if (q.trim()) params.set('q', q.trim());
  if (owned) params.set('owned', owned);
  if (categoryId) params.set('categoryId', categoryId);
  if (vendorId) params.set('vendorId', vendorId);
  const list = useQuery({ queryKey: ['products', params.toString()], queryFn: () => get<ProductPage>(`/catalogue/products?${params}`) });
  const cats = useQuery({ queryKey: ['categories'], queryFn: () => get<{ items: CategoryDto[]; uncategorised: number }>('/catalogue/categories') });
  const vendors = useQuery({ queryKey: ['vendors'], queryFn: () => get<VendorDto[]>('/catalogue/vendors') });
  const pages = list.data ? Math.max(1, Math.ceil(list.data.total / list.data.perPage)) : 1;

  return (
    <div className="space-y-4">
      <PageTitle
        title="Produkter"
        subtitle="Produkter fra 3x synkroniseres, indtil de redigeres her; derefter ejes de af TheBBSystem"
        actions={can('catalogue.edit') && <Link to="/products/$id" params={{ id: 'new' }} className="btn btn-primary">Nyt produkt</Link>}
      />
      <div className="flex flex-wrap gap-2">
        <input className="input w-64" placeholder="Søg navn, varenr. eller SKU" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
        <select className="input w-48" value={categoryId} onChange={(e) => { setCategoryId(e.target.value); setPage(1); }}>
          <option value="">Alle kategorier</option>
          {cats.data?.items.map((c) => <option key={c.id} value={c.id}>{c.name.da || c.slug}</option>)}
        </select>
        <select className="input w-48" value={vendorId} onChange={(e) => { setVendorId(e.target.value); setPage(1); }}>
          <option value="">Alle leverandører</option>
          {vendors.data?.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select className="input w-48" value={owned} onChange={(e) => { setOwned(e.target.value); setPage(1); }}>
          <option value="">Alle</option>
          <option value="true">Redigeret her</option>
          <option value="false">Synkroniseret fra 3x</option>
        </select>
      </div>
      <ErrorBox error={list.error} />
      {list.isLoading ? <Spinner /> : (
        <div className="card p-0">
          <table className="table">
            <thead><tr><th className="w-14"></th><th>Produkt</th><th>Varenr.</th><th>Leverandør</th><th className="text-right">Varianter</th><th className="text-right">Pris fra</th><th className="text-right">Shops</th><th>Kilde</th></tr></thead>
            <tbody>
              {list.data?.items.map((p) => (
                <tr key={p.id} className={p.active ? '' : 'opacity-60'}>
                  <td>{p.image ? <img src={p.image.thumbUrl} alt="" className="h-10 w-10 rounded object-contain" /> : p.imageUrl ? <img src={p.imageUrl} alt="" className="h-10 w-10 rounded object-contain" /> : null}</td>
                  <td><Link to="/products/$id" params={{ id: p.id }} className="font-medium text-primary hover:underline">{p.title || '(uden navn)'}</Link>{!p.active && <Badge tone="zinc">inaktiv</Badge>}</td>
                  <td className="font-mono text-xs">{p.number}</td>
                  <td>{p.vendorName ?? ''}</td>
                  <td className="text-right tabular-nums">{p.variantCount}</td>
                  <td className="text-right tabular-nums">{kr(p.priceFromMinor)}</td>
                  <td className="text-right tabular-nums">{p.shopCount}</td>
                  <td>{p.ownedHere ? <Badge tone="blue">her</Badge> : <Badge>3x</Badge>}</td>
                </tr>
              ))}
              {list.data?.items.length === 0 && <tr><td colSpan={8} className="text-muted">Ingen produkter</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {pages > 1 && (
        <div className="flex items-center gap-2 text-sm">
          <button className="btn" disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft className="h-4 w-4" /></button>
          Side {page} af {pages} ({list.data?.total} produkter)
          <button className="btn" disabled={page >= pages} onClick={() => setPage(page + 1)}><ChevronRight className="h-4 w-4" /></button>
        </div>
      )}
    </div>
  );
}

type Draft = Omit<ProductDetailDto, 'id' | 'externalId' | 'slug' | 'ownedHere' | 'imageUrl' | 'shops' | 'images' | 'offerImages' | 'videos' | 'variants'> & { images: ImageDto[]; offerImages: ImageDto[]; videos: VideoDto[]; variants: VariantEditDto[] };

const emptyVariant = (): VariantEditDto => ({ id: null, sku: '', title: '', options: {}, priceMinor: null, compareAtMinor: null, costMinor: null, b2cPriceMinor: null, stepPrices: [], minQty: 1, maxQty: null, stepQty: 1, allowedQty: [], barcode: null, weightG: null, lengthMm: null, widthMm: null, heightMm: null, hsCode: null, countryOfOrigin: null, customsValueMinor: null, active: true });

const stepText = (v: VariantEditDto) => v.stepPrices.map((s) => `${s.minQty}:${kr(s.priceMinor)}`).join('  ');
const parseSteps = (t: string) =>
  t.split(/\s+/).filter(Boolean).map((part) => {
    const [q, p] = part.split(':');
    return { minQty: Number(q), priceMinor: toMinor(p ?? '') ?? 0 };
  }).filter((s) => s.minQty >= 2);

/** CAT-02 to CAT-05: one product with texts, images, categories and variants. */
export function ProductEditPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const isNew = id === 'new';
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { can } = useAuth();
  const editable = can('catalogue.edit');
  const [error, setError] = useState<unknown>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saved, setSaved] = useState(false);
  const detail = useQuery({ queryKey: ['product', id], queryFn: () => get<ProductDetailDto>(`/catalogue/products/${id}`), enabled: !isNew });
  const cats = useQuery({ queryKey: ['categories'], queryFn: () => get<{ items: CategoryDto[]; uncategorised: number }>('/catalogue/categories') });
  const vendors = useQuery({ queryKey: ['vendors'], queryFn: () => get<VendorDto[]>('/catalogue/vendors') });

  useEffect(() => {
    if (isNew) setDraft({ number: null, title: {}, description: {}, vendorId: null, tags: [], active: true, categoryIds: [], images: [], offerImages: [], videos: [], variants: [emptyVariant()] });
    else if (detail.data) {
      const d = detail.data;
      setDraft({ number: d.number, title: d.title, description: d.description, vendorId: d.vendorId, tags: d.tags, active: d.active, categoryIds: d.categoryIds, images: d.images, offerImages: d.offerImages, videos: d.videos, variants: d.variants });
    }
  }, [isNew, detail.data]);

  const save = useMutation({
    mutationFn: (d: Draft) => {
      const body = { number: d.number || null, title: d.title, description: d.description, vendorId: d.vendorId, tags: d.tags, active: d.active, categoryIds: d.categoryIds, imageIds: d.images.map((i) => i.id), offerImageIds: d.offerImages.map((i) => i.id), videoIds: d.videos.map((v) => v.id), variants: d.variants };
      return isNew ? post<ProductDetailDto>('/catalogue/products', body) : put<ProductDetailDto>(`/catalogue/products/${id}`, body);
    },
    onSuccess: (p) => {
      setError(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      qc.setQueryData(['product', p.id], p);
      void qc.invalidateQueries({ queryKey: ['products'] });
      if (isNew) void navigate({ to: '/products/$id', params: { id: p.id } });
    },
    onError: setError,
  });
  const archive = useMutation({ mutationFn: () => post(`/catalogue/products/${id}/archive`), onSuccess: () => { void qc.invalidateQueries({ queryKey: ['products'] }); void navigate({ to: '/products' }); }, onError: setError });

  if (!draft) return detail.error ? <ErrorBox error={detail.error} /> : <Spinner />;
  const set = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });
  const setVariant = (i: number, patch: Partial<VariantEditDto>) => set({ variants: draft.variants.map((v, j) => (j === i ? { ...v, ...patch } : v)) });
  const moveImage = (i: number, d: number) => {
    const list = [...draft.images];
    const [x] = list.splice(i, 1);
    list.splice(Math.max(0, Math.min(list.length, i + d)), 0, x!);
    set({ images: list });
  };
  const num = (s: string) => (s.trim() === '' ? null : Math.max(0, Math.round(Number(s))));

  return (
    <div className="space-y-4">
      <PageTitle
        title={isNew ? 'Nyt produkt' : draft.title.da || detail.data?.number || 'Produkt'}
        subtitle={!isNew && detail.data && (detail.data.ownedHere ? 'Redigeret her: 3x-synkroniseringen ændrer det ikke længere' : `Fra 3x (id ${detail.data.externalId}); når du gemmer, overtager TheBBSystem produktet`)}
        actions={
          <>
            <Link to="/products" className="btn">Tilbage</Link>
            {!isNew && editable && <button className="btn btn-danger" disabled={archive.isPending} onClick={() => confirm('Arkivér produktet? Det forsvinder fra alle shops.') && archive.mutate()}>Arkivér</button>}
            {editable && <button className="btn btn-primary" disabled={save.isPending} onClick={() => save.mutate(draft)}>{save.isPending ? 'Gemmer…' : saved ? 'Gemt' : 'Gem'}</button>}
          </>
        }
      />
      <ErrorBox error={error} />
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="card space-y-3 lg:col-span-2">
          <div>
            <div className="label">Navn</div>
            <I18nInput value={draft.title} onChange={(title) => set({ title })} />
          </div>
          <div>
            <div className="label">Beskrivelse</div>
            <I18nInput value={draft.description} onChange={(description) => set({ description })} html rows={9} />
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <label className="block"><span className="label">Varenummer</span><input className="input font-mono" value={draft.number ?? ''} onChange={(e) => set({ number: e.target.value || null })} /></label>
            <label className="block">
              <span className="label">Leverandør</span>
              <select className="input" value={draft.vendorId ?? ''} onChange={(e) => set({ vendorId: e.target.value || null })}>
                <option value="">Ingen</option>
                {vendors.data?.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </label>
            <label className="block"><span className="label">Tags (komma)</span><input className="input" defaultValue={draft.tags.join(', ')} onBlur={(e) => set({ tags: e.target.value.split(',').map((t) => t.trim()).filter(Boolean) })} /></label>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={draft.active} onChange={(e) => set({ active: e.target.checked })} /> Aktiv (kan sælges)</label>
        </div>
        <div className="space-y-4">
          <div className="card space-y-2">
            <div className="label">Billeder</div>
            <div className="grid grid-cols-3 gap-2">
              {draft.images.map((img, i) => (
                <div key={img.id} className="group relative rounded border border-line p-1">
                  <img src={img.thumbUrl} alt="" className="aspect-square w-full object-contain" />
                  {i === 0 && <span className="absolute left-1 top-1 rounded bg-primary px-1 text-[10px] text-white">forside</span>}
                  <div className="flex justify-between text-muted">
                    <button type="button" disabled={i === 0} onClick={() => moveImage(i, -1)}><ChevronLeft className="h-4 w-4" /></button>
                    <button type="button" onClick={() => set({ images: draft.images.filter((x) => x.id !== img.id) })}><Trash2 className="h-4 w-4" /></button>
                    <button type="button" disabled={i === draft.images.length - 1} onClick={() => moveImage(i, 1)}><ChevronRight className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
            {draft.images.length === 0 && detail.data?.imageUrl && <div className="text-xs text-muted">3x-billede: <img src={detail.data.imageUrl} alt="" className="mt-1 h-16 object-contain" /></div>}
            {editable && <ImageUploadButton onUploaded={(img) => setDraft((d) => (d ? { ...d, images: [...d.images, img] } : d))} onError={setError} />}
          </div>
          <div className="card space-y-2">
            <div className="label">Videoer</div>
            {draft.videos.map((v) => (
              <div key={v.id} className="flex items-center gap-2">
                <video src={v.url} controls preload="metadata" className="h-24 flex-1 rounded bg-black" />
                {editable && <button type="button" className="text-muted" onClick={() => set({ videos: draft.videos.filter((x) => x.id !== v.id) })}><Trash2 className="h-4 w-4" /></button>}
              </div>
            ))}
            {editable && <VideoUploadButton onUploaded={(v) => setDraft((d) => (d ? { ...d, videos: [...d.videos, v] } : d))} onError={setError} />}
          </div>
          <div className="card space-y-2">
            <div className="label">Tilbudsbilleder</div>
            <p className="text-xs text-muted">Bruges i tilbuds-PDF'er til virksomheder; vises aldrig i en shop.</p>
            <div className="grid grid-cols-3 gap-2">
              {draft.offerImages.map((img) => (
                <div key={img.id} className="relative rounded border border-line p-1">
                  <img src={img.thumbUrl} alt="" className="aspect-square w-full object-contain" />
                  {editable && <button type="button" className="absolute right-1 top-1 rounded bg-white/80 text-muted" onClick={() => set({ offerImages: draft.offerImages.filter((x) => x.id !== img.id) })}><Trash2 className="h-4 w-4" /></button>}
                </div>
              ))}
            </div>
            {editable && <ImageUploadButton label="Upload tilbudsbillede" onUploaded={(img) => setDraft((d) => (d ? { ...d, offerImages: [...d.offerImages, img] } : d))} onError={setError} />}
          </div>
          <div className="card space-y-1">
            <div className="label">Kategorier</div>
            {cats.data?.items.length === 0 && <div className="text-sm text-muted">Ingen kategorier endnu. <Link to="/categories" className="text-primary hover:underline">Opret</Link></div>}
            {cats.data?.items.map((c) => (
              <label key={c.id} className="flex items-center gap-2 text-sm" style={{ paddingLeft: c.parentId ? 16 : 0 }}>
                <input type="checkbox" checked={draft.categoryIds.includes(c.id)} onChange={(e) => set({ categoryIds: e.target.checked ? [...draft.categoryIds, c.id] : draft.categoryIds.filter((x) => x !== c.id) })} />
                {c.name.da || c.slug}
              </label>
            ))}
          </div>
          {!isNew && detail.data && detail.data.shops.length > 0 && (
            <div className="card text-sm">
              <div className="label">I shops</div>
              {detail.data.shops.map((s) => <div key={s.shopId}><Link to="/shops/$id/storefront" params={{ id: s.shopId }} className="text-primary hover:underline">{s.shopName}</Link></div>)}
            </div>
          )}
        </div>
      </div>

      <section className="card space-y-2 overflow-x-auto">
        <div className="flex items-center justify-between">
          <div className="label">Varianter, priser og mængder</div>
          {editable && <button className="btn" onClick={() => set({ variants: [...draft.variants, emptyVariant()] })}>Tilføj variant</button>}
        </div>
        {/* Fixed columns, and every field fills its column, so fields sit right under their headers. The variant name takes the spare width. */}
        <div className="overflow-x-auto">
          <table className="table w-full min-w-[100rem] table-fixed text-sm [&_td]:px-2 [&_th]:px-2">
            <colgroup>
              <col className="w-32" /><col /><col className="w-24" /><col className="w-24" /><col className="w-24" /><col className="w-24" /><col className="w-40" /><col className="w-36" /><col className="w-32" /><col className="w-32" /><col className="w-20" /><col className="w-28" /><col className="w-20" /><col className="w-14" />
            </colgroup>
            <thead>
              <tr><th>SKU</th><th>Variant</th><th className="text-right">Pris</th><th className="text-right">Før-pris</th><th className="text-right">Kostpris</th><th className="text-right">B2C-pris</th><th>Mængderabat (antal:pris)</th><th>Min / maks / trin</th><th title="Kun disse antal kan bestilles, fx 1-14, 28, 42">Tilladte antal</th><th>Stregkode</th><th className="text-right">Vægt g</th><th title="Toldtarifnummer til proforma">HS-kode</th><th title="Oprindelsesland, to bogstaver (ISO), fx CN">Oprindelse</th><th className="text-center">Aktiv</th></tr>
            </thead>
            <tbody>
              {draft.variants.map((v, i) => (
                <tr key={v.id ?? `new-${i}`}>
                  <td><input className="input w-full font-mono text-xs" value={v.sku} disabled={!!v.id && !isNew && !!detail.data?.externalId} onChange={(e) => setVariant(i, { sku: e.target.value })} /></td>
                  <td><input className="input w-full" value={v.title} onChange={(e) => setVariant(i, { title: e.target.value })} /></td>
                  <td><MoneyInput className="input w-full text-right" value={v.priceMinor} onChange={(priceMinor) => setVariant(i, { priceMinor })} /></td>
                  <td><MoneyInput className="input w-full text-right" value={v.compareAtMinor} onChange={(compareAtMinor) => setVariant(i, { compareAtMinor })} /></td>
                  <td><MoneyInput className="input w-full text-right" value={v.costMinor} onChange={(costMinor) => setVariant(i, { costMinor })} /></td>
                  <td><MoneyInput className="input w-full text-right" value={v.b2cPriceMinor} onChange={(b2cPriceMinor) => setVariant(i, { b2cPriceMinor })} /></td>
                  <td><input className="input w-full text-xs" placeholder="10:150,00 50:120,00" defaultValue={stepText(v)} onBlur={(e) => setVariant(i, { stepPrices: parseSteps(e.target.value) })} /></td>
                  <td>
                    <div className="flex gap-1">
                      <input className="input w-0 min-w-0 flex-1" type="number" min={1} title="Min" value={v.minQty} onChange={(e) => setVariant(i, { minQty: Math.max(1, Number(e.target.value) || 1) })} />
                      <input className="input w-0 min-w-0 flex-1" type="number" min={1} title="Maks" value={v.maxQty ?? ''} onChange={(e) => setVariant(i, { maxQty: num(e.target.value) || null })} />
                      <input className="input w-0 min-w-0 flex-1" type="number" min={1} title="Trin" value={v.stepQty} onChange={(e) => setVariant(i, { stepQty: Math.max(1, Number(e.target.value) || 1) })} />
                    </div>
                  </td>
                  <td><input className="input w-full text-xs" placeholder="alle" title="Kommasepareret; 1-14 betyder 1 til 14" defaultValue={qtyText(v.allowedQty ?? [])} onBlur={(e) => setVariant(i, { allowedQty: parseQty(e.target.value) })} /></td>
                  <td><input className="input w-full font-mono text-xs" value={v.barcode ?? ''} onChange={(e) => setVariant(i, { barcode: e.target.value || null })} /></td>
                  <td><input className="input w-full text-right" type="number" min={0} value={v.weightG ?? ''} onChange={(e) => setVariant(i, { weightG: num(e.target.value) })} /></td>
                  <td><input className="input w-full font-mono text-xs" maxLength={20} value={v.hsCode ?? ''} onChange={(e) => setVariant(i, { hsCode: e.target.value.trim() || null })} /></td>
                  <td><input className="input w-full font-mono text-xs uppercase" maxLength={2} placeholder="CN" value={v.countryOfOrigin ?? ''} onChange={(e) => setVariant(i, { countryOfOrigin: e.target.value.replace(/[^a-z]/gi, '').toUpperCase() || null })} /></td>
                  <td className="text-center">
                    <input type="checkbox" checked={v.active} onChange={(e) => setVariant(i, { active: e.target.checked })} />
                    {!v.id && draft.variants.length > 1 && <button className="ml-2 text-muted" onClick={() => set({ variants: draft.variants.filter((_, j) => j !== i) })}><Trash2 className="inline h-4 w-4" /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted">Priser i kr. ekskl. moms. Shop-priser sættes på shoppens sortiment. HS-kode og oprindelse bruges på proformafakturaer; mangler de her, bruges Navisions. Mål redigeres under <Link to="/stock" className="text-primary hover:underline">Beholdning</Link>.</p>
      </section>

      {detail.data?.variants.filter((v) => v.components?.length || /^\s*SAM\s*[;:]/i.test(v.sku)).map((v) => (
        <BundleEditor key={v.id} productId={detail.data!.id} variant={v} editable={editable} />
      ))}
    </div>
  );
}

/** A sampak: one item for the customer and the invoice, picked and stocked as its components. */
function BundleEditor({ productId, variant, editable }: { productId: string; variant: ProductDetailDto['variants'][number]; editable: boolean }) {
  const qc = useQueryClient();
  const text = (variant.components ?? []).map((c) => `${c.quantity} ${c.sku}`).join('\n');
  const [draft, setDraft] = useState(text);
  useEffect(() => setDraft(text), [text]);
  const save = useMutation({
    mutationFn: () =>
      put(`/catalogue/variants/${variant.id}/components`, {
        components: draft
          .split('\n')
          .map((l) => l.trim())
          .filter(Boolean)
          .map((l) => {
            const m = /^(\d+)\s*[x×*]?\s+(.+)$/i.exec(l);
            return m ? { quantity: Number(m[1]), sku: m[2]!.trim() } : { quantity: 1, sku: l };
          }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['product', productId] }),
  });
  const built = (variant.components ?? []).length > 0;
  return (
    <section className="card space-y-3 text-sm">
      <div className="flex items-center justify-between">
        <div className="label">Sampak <span className="font-mono normal-case">{variant.sku}</span></div>
        {built ? <Badge tone="green">{variant.components!.length} varer</Badge> : <Badge tone="amber">Ikke opløst</Badge>}
      </div>
      <p className="text-xs text-muted">
        Sælges og faktureres som én vare. Lager, pluk og forsendelse går på varerne herunder: der kan sælges så mange sæt, som den knappeste vare rækker til.
        {!built && ' Varenumrene i SKU’en findes ikke alle i systemet eller i Navision endnu; ret listen og gem, eller vent på næste Navision-synkronisering.'}
      </p>
      {built && (
        <ul className="space-y-1">
          {variant.components!.map((c) => <li key={c.sku} className="flex gap-3"><span className="w-8 tabular-nums text-muted">{c.quantity}×</span><span className="font-mono text-xs">{c.sku}</span><span>{c.title}</span></li>)}
        </ul>
      )}
      {editable && (
        <div className="space-y-2">
          <textarea className="input h-28 font-mono text-xs" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={'1 RC1018213\n2 RC1071673'} />
          <div className="flex items-center gap-3">
            <button className="btn" disabled={save.isPending || draft === text} onClick={() => save.mutate()}>Gem indhold</button>
            <span className="text-xs text-muted">En linje pr. vare: antal og varenummer. Tom liste gør varen til en almindelig vare.</span>
          </div>
          {save.error && <ErrorBox error={save.error} />}
        </div>
      )}
    </section>
  );
}

/** Sampakker from 3x whose components could not be found: fix them in the product editor, then try again. */
export function UnresolvedBundlesPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [onlyOpen, setOnlyOpen] = useState(false);
  const list = useQuery({ queryKey: ['bundles-unresolved'], queryFn: () => get<UnresolvedBundleDto[]>('/catalogue/variants/bundles/unresolved') });
  const rebuild = useMutation({
    mutationFn: () => post<RebuildBundlesResultDto>('/catalogue/variants/bundles/rebuild'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['bundles-unresolved'] }),
  });
  const needle = q.trim().toLowerCase();
  const rows = (list.data ?? []).filter((r) => (!onlyOpen || r.openOrderLines > 0) && (!needle || r.sku.toLowerCase().includes(needle) || r.productTitle.toLowerCase().includes(needle) || r.unknown.some((u) => u.toLowerCase().includes(needle))));
  const withOrders = (list.data ?? []).filter((r) => r.openOrderLines > 0).length;

  return (
    <div className="space-y-4">
      <PageTitle
        title="Uopløste sampakker"
        subtitle="Sampakker fra 3x, hvor ikke alle varenumre i SKU’en findes i systemet eller i Navision. De lagerføres og plukkes ikke som sæt, før indholdet er rettet."
        actions={can('catalogue.edit') && <button className="btn btn-primary" disabled={rebuild.isPending} onClick={() => rebuild.mutate()}>{rebuild.isPending ? 'Prøver…' : 'Prøv igen'}</button>}
      />
      {rebuild.data && (
        <div className="card text-sm">
          {rebuild.data.checked === 0 ? 'Ingen sampakker at opløse.' : `${rebuild.data.built} af ${rebuild.data.checked} opløst; ${rebuild.data.unresolved.length} har stadig ukendte varenumre.`}
        </div>
      )}
      <ErrorBox error={rebuild.error} />
      <div className="flex flex-wrap items-center gap-3">
        <input className="input w-64" placeholder="Søg SKU, produkt eller varenr." value={q} onChange={(e) => setQ(e.target.value)} />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} /> Kun med åbne ordrer</label>
        {list.data && <span className="text-sm text-muted">{list.data.length} uopløste, {withOrders} på åbne ordrer</span>}
      </div>
      <ErrorBox error={list.error} />
      {list.isLoading ? <Spinner /> : list.data && (
        <div className="card p-0">
          <table className="table">
            <thead><tr><th>Produkt</th><th>SKU</th><th className="text-right">Åbne ordrelinjer</th><th>Ukendte varenumre</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.variantId} className={r.active ? '' : 'opacity-60'}>
                  <td>
                    <Link to="/products/$id" params={{ id: r.productId }} className="font-medium text-primary hover:underline">{r.productTitle || '(uden navn)'}</Link>
                    {r.variantTitle && <span className="text-muted"> – {r.variantTitle}</span>}
                    {!r.active && <Badge tone="zinc">inaktiv</Badge>}
                  </td>
                  <td className="break-all font-mono text-xs">{r.sku}</td>
                  <td className="text-right tabular-nums">{r.openOrderLines || ''}</td>
                  <td>
                    {r.unknown.length
                      ? <div className="flex flex-wrap gap-1">{r.unknown.map((u) => <Badge key={u} tone="amber"><span className="font-mono normal-case">{u}</span></Badge>)}</div>
                      : <span className="text-xs text-muted">Alle kendt nu; tryk Prøv igen</span>}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={4} className="text-muted">{list.data?.length ? 'Ingen match' : 'Alle sampakker er opløst'}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// --- categories and vendors ------------------------------------------------------------------------

type CatForm = { id: string | null; parentId: string | null; slug: string; name: I18n; description: I18n; image: ImageDto | null; sortOrder: number; active: boolean };

/** CAT-06 and CAT-07: category tree and vendors. */
export function CategoriesPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const editable = can('catalogue.edit');
  const [error, setError] = useState<unknown>(null);
  const [form, setForm] = useState<CatForm | null>(null);
  const [vendorName, setVendorName] = useState('');
  const cats = useQuery({ queryKey: ['categories'], queryFn: () => get<{ items: CategoryDto[]; uncategorised: number }>('/catalogue/categories') });
  const vendors = useQuery({ queryKey: ['vendors'], queryFn: () => get<VendorDto[]>('/catalogue/vendors') });
  const saveCat = useMutation({
    mutationFn: (f: CatForm) => {
      const body = { parentId: f.parentId, slug: f.slug || undefined, name: f.name, description: f.description, imageId: f.image?.id ?? null, sortOrder: f.sortOrder, active: f.active };
      return f.id ? put(`/catalogue/categories/${f.id}`, body) : post('/catalogue/categories', body);
    },
    onSuccess: (data) => { qc.setQueryData(['categories'], data); setForm(null); setError(null); },
    onError: setError,
  });
  const deleteCat = useMutation({ mutationFn: (id: string) => del(`/catalogue/categories/${id}`), onSuccess: (data) => { qc.setQueryData(['categories'], data); setForm(null); }, onError: setError });
  const saveVendor = useMutation({
    mutationFn: (v: { id: string | null; name: string; active: boolean }) => (v.id ? put<VendorDto[]>(`/catalogue/vendors/${v.id}`, v) : post<VendorDto[]>('/catalogue/vendors', v)),
    onSuccess: (data) => { qc.setQueryData(['vendors'], data); setVendorName(''); setError(null); },
    onError: setError,
  });

  const items = cats.data?.items ?? [];
  const roots = items.filter((c) => !c.parentId);
  const childrenOf = (id: string) => items.filter((c) => c.parentId === id);
  const open = (c: CategoryDto | null, parentId: string | null = null) =>
    setForm(c ? { id: c.id, parentId: c.parentId, slug: c.slug, name: c.name, description: c.description, image: c.image, sortOrder: c.sortOrder, active: c.active } : { id: null, parentId, slug: '', name: {}, description: {}, image: null, sortOrder: 100, active: true });
  const row = (c: CategoryDto, depth: number) => (
    <div key={c.id}>
      <div className="flex items-center justify-between border-b border-line py-1.5 text-sm" style={{ paddingLeft: depth * 20 }}>
        <span className="flex items-center gap-2">
          {c.image && <img src={c.image.thumbUrl} alt="" className="h-6 w-6 rounded object-cover" />}
          <button className="font-medium text-primary hover:underline" onClick={() => open(c)} disabled={!editable}>{c.name.da || c.slug}</button>
          <span className="text-xs text-muted">/{c.slug} · {c.productCount} produkter</span>
          {!c.active && <Badge>skjult</Badge>}
        </span>
        {editable && depth === 0 && <button className="text-xs text-primary hover:underline" onClick={() => open(null, c.id)}>+ underkategori</button>}
      </div>
      {childrenOf(c.id).map((x) => row(x, depth + 1))}
    </div>
  );

  return (
    <div className="space-y-4">
      <PageTitle title="Kategorier og leverandører" subtitle={cats.data ? `${cats.data.uncategorised} aktive produkter har ingen kategori` : undefined} actions={editable && <button className="btn btn-primary" onClick={() => open(null)}>Ny kategori</button>} />
      <ErrorBox error={error ?? cats.error} />
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="card lg:col-span-2">
          <div className="label">Kategorier</div>
          {cats.isLoading ? <Spinner /> : roots.length === 0 ? <div className="text-sm text-muted">Ingen kategorier endnu.</div> : roots.map((c) => row(c, 0))}
        </div>
        <div className="card space-y-2">
          <div className="label">Leverandører / brands</div>
          {vendors.data?.map((v) => (
            <div key={v.id} className="flex items-center justify-between border-b border-line py-1 text-sm">
              <span className={v.active ? '' : 'text-muted line-through'}>{v.name}</span>
              <span className="flex items-center gap-2 text-xs text-muted">
                {v.productCount}
                {editable && <button className="text-primary hover:underline" onClick={() => saveVendor.mutate({ id: v.id, name: v.name, active: !v.active })}>{v.active ? 'skjul' : 'vis'}</button>}
              </span>
            </div>
          ))}
          {editable && (
            <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (vendorName.trim()) saveVendor.mutate({ id: null, name: vendorName.trim(), active: true }); }}>
              <input className="input" placeholder="Ny leverandør" value={vendorName} onChange={(e) => setVendorName(e.target.value)} />
              <button className="btn">Tilføj</button>
            </form>
          )}
        </div>
      </div>
      {form && (
        <Modal title={form.id ? 'Redigér kategori' : 'Ny kategori'} onClose={() => setForm(null)}>
          <div className="space-y-3">
            <div><div className="label">Navn</div><I18nInput value={form.name} onChange={(name) => setForm({ ...form, name })} /></div>
            <div><div className="label">Beskrivelse</div><I18nInput value={form.description} onChange={(description) => setForm({ ...form, description })} multiline rows={3} /></div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="label">Under</span>
                <select className="input" value={form.parentId ?? ''} onChange={(e) => setForm({ ...form, parentId: e.target.value || null })}>
                  <option value="">(øverste niveau)</option>
                  {roots.filter((c) => c.id !== form.id).map((c) => <option key={c.id} value={c.id}>{c.name.da || c.slug}</option>)}
                </select>
              </label>
              <label className="block"><span className="label">Adresse (slug)</span><input className="input" placeholder="dannes af navnet" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} /></label>
              <label className="block"><span className="label">Rækkefølge</span><input className="input" type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) || 0 })} /></label>
              <label className="flex items-end gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Vises i shops</label>
            </div>
            <div><div className="label">Billede</div><SingleImage image={form.image} onChange={(image) => setForm({ ...form, image })} onError={setError} /></div>
            <div className="flex justify-between">
              {form.id ? <button className="btn btn-danger" onClick={() => confirm('Slet kategorien?') && deleteCat.mutate(form.id!)}>Slet</button> : <span />}
              <button className="btn btn-primary" disabled={saveCat.isPending || !(form.name.da || form.name.en)} onClick={() => saveCat.mutate(form)}>Gem</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/** Allowed quantities as text: runs of consecutive numbers shown as "1-14". */
function qtyText(list: number[]): string {
  const out: string[] = [];
  for (let i = 0; i < list.length; ) {
    let j = i;
    while (j + 1 < list.length && list[j + 1] === list[j]! + 1) j++;
    out.push(j - i >= 2 ? `${list[i]}-${list[j]}` : list.slice(i, j + 1).join(', '));
    i = j + 1;
  }
  return out.join(', ');
}

/** "1-14, 28, 42" to [1..14, 28, 42], sorted, without duplicates. */
function parseQty(text: string): number[] {
  const out = new Set<number>();
  for (const part of text.split(/[,;\s]+/).filter(Boolean)) {
    const m = /^(\d+)-(\d+)$/.exec(part);
    if (m) for (let n = Number(m[1]); n <= Math.min(Number(m[2]), Number(m[1]) + 500); n++) out.add(n);
    else if (/^\d+$/.test(part) && Number(part) > 0) out.add(Number(part));
  }
  return [...out].sort((a, b) => a - b).slice(0, 500);
}
